/**
 * DiffTreeViewer
 *
 * Extracted from HierarchicalMetadataExample for production use.
 * Renders a recursive diff tree with expand/collapse and color-coded changes.
 *
 * Used by FormSchemaOverrideDiffPanel to visualize schema diffs.
 */

import { Text } from '@mantine/core';
import type { DiffNode } from '../utils/diff-utils';

// =============================================================================
// TYPES
// =============================================================================

export interface DiffTreeViewerProps {
  /** Root diff node from buildRecursiveDiff() */
  node: DiffNode;
  /** When true, only show nodes that have changes (hide 'same' leaves) */
  showOnlyChanges?: boolean;
  /** Map of path → expanded state. Default: all expanded. */
  expandedPaths: Record<string, boolean>;
  /** Callback when a node is toggled */
  onTogglePath: (path: string) => void;
}

// =============================================================================
// COLOR MAPPING
// =============================================================================

const KIND_COLORS: Record<DiffNode['kind'], string> = {
  added: '#2b8a3e',
  removed: '#d6336c',
  changed: '#e67700',
  nested: '#495057',
  same: '#868e96',
};

// =============================================================================
// COMPONENT
// =============================================================================

export function DiffTreeViewer({
  node,
  showOnlyChanges = true,
  expandedPaths,
  onTogglePath,
}: DiffTreeViewerProps) {
  return <DiffNodeRenderer node={node} depth={0} showOnlyChanges={showOnlyChanges} expandedPaths={expandedPaths} onTogglePath={onTogglePath} />;
}

// =============================================================================
// INTERNAL NODE RENDERER
// =============================================================================

interface DiffNodeRendererProps {
  node: DiffNode;
  depth: number;
  showOnlyChanges: boolean;
  expandedPaths: Record<string, boolean>;
  onTogglePath: (path: string) => void;
}

function DiffNodeRenderer({ node, depth, showOnlyChanges, expandedPaths, onTogglePath }: DiffNodeRendererProps) {
  const pad = { paddingLeft: depth * 12 };
  const isChanged = node.kind !== 'same';
  const color = KIND_COLORS[node.kind];
  const label = node.path || '(root)';
  const nodeKey = node.path || '(root)';

  // Leaf node (no children)
  if (!node.children) {
    if (showOnlyChanges && !isChanged) return null;
    return (
      <div style={pad}>
        <Text component="span" size="xs" fw={600} c={color}>[{node.kind}]</Text>
        {' '}
        <Text component="span" size="xs">{label}</Text>
        {node.kind === 'changed' && (
          <Text component="span" size="xs" c="dimmed">
            {' '}{JSON.stringify(node.from)} → {JSON.stringify(node.to)}
          </Text>
        )}
        {node.kind === 'added' && (
          <Text component="span" size="xs" c="dimmed"> {JSON.stringify(node.to)}</Text>
        )}
        {node.kind === 'removed' && (
          <Text component="span" size="xs" c="dimmed" td="line-through"> {JSON.stringify(node.from)}</Text>
        )}
      </div>
    );
  }

  // Branch node — filter children if showOnlyChanges
  const visibleChildren = showOnlyChanges && !isChanged
    ? [] // entire subtree is 'same', skip
    : node.children;

  if (showOnlyChanges && visibleChildren.length === 0) return null;

  const isOpen = expandedPaths[nodeKey] ?? true;

  return (
    <div>
      <div
        style={{ ...pad, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => onTogglePath(nodeKey)}
      >
        <Text component="span" size="xs" fw={600} c={color}>
          {isOpen ? '\u25BC' : '\u25B6'} [{node.kind}]
        </Text>
        {' '}
        <Text component="span" size="xs">{label}</Text>
      </div>
      {isOpen && visibleChildren.map(ch => (
        <DiffNodeRenderer
          key={ch.path || `${nodeKey}.${ch.path}`}
          node={ch}
          depth={depth + 1}
          showOnlyChanges={showOnlyChanges}
          expandedPaths={expandedPaths}
          onTogglePath={onTogglePath}
        />
      ))}
    </div>
  );
}
